import React, { useState } from 'react';
import { MoreOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { Col, Dropdown, message, Modal, Pagination, Row, Empty, Button, MenuProps } from 'antd';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useGetAllGroupMembersQuery, useRemoveGroupMembersMutation } from '../../../graphql/group.generated';
import { CorpUser, EntityType } from '../../../types.generated';
import { CustomAvatar } from '../../shared/avatar';
import { useEntityRegistry } from '../../useEntityRegistry';
import { AddGroupMembersModal } from './AddGroupMembersModal';
import { useTranslation } from '../../../i18n/useTranslation';
import { scrollToTop } from '../../shared/searchUtils';

const AVATAR_STYLE = { margin: '5px 5px 5px 0' };

/**
 * Styled Components
 */

const MemberNameSection = styled.div`
    font-size: 20px;
    line-height: 28px;
    color: #262626;
    display: flex;
    align-items: center;
    justify-content: start;
    padding-left: 12px;
`;

const GroupMemberWrapper = styled.div`
    background-color: #ffffff;

    & .groupMemberRow {
        margin: 0 19px;
    }
`;

const MemberColumn = styled(Col)`
    padding: 19px 0 19px 0;
    border-bottom: 1px solid #f0f0f0;

    a {
        display: block;
    }
`;

const MemberEditIcon = styled.div`
    font-size: 22px;
    float: right;
`;

const Name = styled.span`
    margin-left: 8px;
`;

const NoGroupMembers = styled(Empty)`
    padding: 40px;
`;

type Props = {
    urn: string;
    pageSize: number;
    isExternalGroup: boolean;
    onChangeMembers?: () => void;
};

export default function GroupMembers({ urn, pageSize, isExternalGroup, onChangeMembers }: Props) {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();

    const [page, setPage] = useState(1);
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const [isEditingMembers, setIsEditingMembers] = useState(false);
    const start = (page - 1) * pageSize;
    const { data: membersData, refetch } = useGetAllGroupMembersQuery({
        variables: { urn, start, count: pageSize },
    });
    const [removeGroupMembersMutation] = useRemoveGroupMembersMutation();

    const onChangeMembersPage = (newPage: number) => {
        scrollToTop();
        setPage(newPage);
    };

    const removeGroupMember = (userUrn: string) => {
        removeGroupMembersMutation({
            variables: {
                groupUrn: urn,
                userUrns: [userUrn],
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({ content: t('Removed Group Member!'), duration: 2 });
                    onChangeMembers?.();
                    // Hack to deal with eventual consistency
                    setTimeout(() => {
                        // Reload the page.
                        refetch();
                    }, 2000);
                }
            })
            .catch((e) => {
                message.destroy();
                message.error({ content: `${t('Failed to remove group member:')} \n ${e.message || ''}`, duration: 3 });
            });
    };

    const onClickEditMembers = () => {
        setIsEditingMembers(true);
    };

    const onAddMembers = () => {
        onChangeMembers?.();
        setTimeout(() => {
            // Reload the page.
            refetch();
        }, 3000);
    };

    const onRemoveMember = (memberUrn: string) => {
        Modal.confirm({
            title: t('Confirm Group Member Removal'),
            content: t('Are you sure you want to remove this user from the group?'),
            onOk() {
                removeGroupMember(memberUrn);
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            centered: true,
            maskClosable: true,
            closable: true,
        });
    };

    const relationships = membersData && membersData.corpGroup?.relationships;
    const total = relationships?.total || 0;
    const groupMembers = relationships?.relationships?.map((rel) => rel.entity as CorpUser) || [];

    const getItems = (urnID: string): MenuProps['items'] => {
        return [
            {
                key: 'make',
                disabled: true,
                label: (
                    <span>
                        <UserAddOutlined /> {t('Make owner')}
                    </span>
                ),
            },
            {
                key: 'remove',
                disabled: isExternalGroup,
                onClick: () => onRemoveMember(urnID),
                label: (
                    <span>
                        <UserDeleteOutlined /> {t('Remove from Group')}
                    </span>
                ),
            },
        ];
    };

    return (
        <>
            <div className="f-bottom-border px-2 pb-2">
                <Button type="text" className="f-btn-flat" disabled={isExternalGroup} onClick={onClickEditMembers}>
                    <UserAddOutlined />
                    <span>{t('Add Member')}</span>
                </Button>
            </div>
            <GroupMemberWrapper>
                {groupMembers.length === 0 && <NoGroupMembers description={t('No members in this group yet.')} />}
                {groupMembers &&
                    groupMembers.map((item) => {
                        const entityUrn = entityRegistry.getEntityUrl(EntityType.CorpUser, item.urn);
                        return (
                            <Row className="groupMemberRow" align="middle" key={entityUrn}>
                                <MemberColumn xl={23} lg={23} md={23} sm={23} xs={23}>
                                    <Link to={entityUrn}>
                                        <MemberNameSection>
                                            <CustomAvatar
                                                useDefaultAvatar={false}
                                                size={28}
                                                email={item?.username}
                                                photoUrl={
                                                    item.editableProperties?.pictureLink
                                                        ? `${item.editableProperties.pictureLink}&s=thumbnail_100`
                                                        : ''
                                                }
                                                name={entityRegistry.getDisplayName(EntityType.CorpUser, item)}
                                                style={AVATAR_STYLE}
                                            />
                                            <Name className="f-text-medium-content f-color-dark-black-s100 f-text-semibold">
                                                {entityRegistry.getDisplayName(EntityType.CorpUser, item)}
                                            </Name>
                                        </MemberNameSection>
                                    </Link>
                                </MemberColumn>
                                <MemberColumn xl={1} lg={1} md={1} sm={1} xs={1}>
                                    <MemberEditIcon>
                                        <Dropdown menu={{ items: getItems(item.urn) }}>
                                            <MoreOutlined />
                                        </Dropdown>
                                    </MemberEditIcon>
                                </MemberColumn>
                            </Row>
                        );
                    })}
            </GroupMemberWrapper>
            <Row justify="center" className="my-2">
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    showLessItems
                    onChange={onChangeMembersPage}
                    showSizeChanger={false}
                />
            </Row>
            {isEditingMembers && (
                <AddGroupMembersModal
                    urn={urn}
                    visible={isEditingMembers}
                    onSubmit={onAddMembers}
                    onCloseModal={() => setIsEditingMembers(false)}
                />
            )}
        </>
    );
}
